import React from 'react'
import { mkWidget, Action, Pair, AsyncState, async, loadingAsyncState } from 'widgets-for-react'
import { CustomPagesState, PublicSiteCustomRoutes } from './public_site/custom_pages/types.mod'
import { CustomRouteParams } from './cms/router/type.gen'
import { PublicSiteState } from './public_site.main.mod'
import { WordPress } from './wordpress/wordpress_api'
import { AxiosResponse } from 'axios'
import { t } from 'i18next'
import { SearchForm } from './public_site/templates/searchbar'
import { Link } from 'react-router-dom'
import { linkWrapper } from './cms/blocks/utils.gen'
import { toRelativeUrl, decodeHTMLEntities } from '../helpers'
import { PaginationResponse, Pager } from './wordpress/types_and_parser/extra_types.mod'
import { Pagination } from './public_site/templates/pagination.mod'

function capitalizeFirst(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function fixFaqLink(url: string, slug: string): string {
  return url.replace(`/${slug}`, `#${slug}`)
}

const searchResults = (
  s0: Pair<CustomRouteParams<PublicSiteState, PublicSiteCustomRoutes>, CustomPagesState['search']>
) =>
  mkWidget({
    run: (setState: (_: Action<CustomPagesState['search']>) => void) => {
      return (
        <article className="page page--search-results">
          <div className="container--m page__inner grid--block">
            <header className="page__header">
              <h1 className="page__title typo--xl">{t('searchResults.title')}</h1>
            </header>
            <div className="page__content">
              <SearchForm
                searchValue={s0.snd.header.searchValue}
                updateSearchValue={(e) =>
                  setState((s1) => ({
                    ...s1,
                    header: { ...s1.header, searchValue: e },
                  }))
                }
                label={t('searchResults.formLabel')}
              />
              <section className="results">
                <div className="results__inner">
                  <div className="results__content">
                    {s0.snd.header.searchValue == '' ? (
                      <p>{t('searchResults.noSearchTerm')}</p>
                    ) : s0.snd.request.kind != 'loaded' ? (
                      <>{s0.snd.request.kind}</>
                    ) : s0.snd.response ? (
                      <>
                        <ul className="results__list list">
                          {s0.snd.response.map((search_result) => (
                            <li className="list__item">
                              <Link to={toRelativeUrl(search_result.link)} className="list__link">
                                {search_result.type && (
                                  <span className="list__label typo--3xs">
                                    {t(`searchResults.types.${search_result.type}`)}
                                  </span>
                                )}
                                <h2 className="list__title typo--m">
                                  {decodeHTMLEntities(search_result.title.rendered)}
                                </h2>
                                {search_result.excerpt.rendered && (
                                  <div
                                    className="list__description"
                                    dangerouslySetInnerHTML={{
                                      __html: decodeHTMLEntities(search_result.excerpt.rendered),
                                    }}
                                  />
                                )}
                              </Link>
                            </li>
                          ))}
                        </ul>
                        <Pagination
                          pager={s0.snd.pager}
                          setPage={(page: number) =>
                            setState((s) => ({
                              ...s,
                              request: loadingAsyncState(async () => {
                                let result: AxiosResponse<PaginationResponse<
                                  WordPress.PostTypeResponse[]
                                >> = {} as AxiosResponse<PaginationResponse<WordPress.PostTypeResponse[]>>
                                if (s0.snd.header.searchValue) {
                                  result = await WordPress.api().search(s0.snd.header.searchValue, page)
                                }
                                return { kind: 'result', status: 200, value: result }
                              }),
                              response: null,
                              pager: { ...s.pager, current_page: page },
                            }))
                          }
                        />
                      </>
                    ) : (
                      <div className="text--styled">
                        <p>{t('searchResults.noResults')}</p>
                        <ul>
                          <li>{t('searchResults.noResultsTips.spelling')}</li>
                          <li>{t('searchResults.noResultsTips.describe')}</li>
                          <li>{t('searchResults.noResultsTips.shorter')}</li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {async<AxiosResponse<PaginationResponse<WordPress.PostTypeResponse[]>>>()(s0.snd.request).run(
                  (a0: Action<CustomPagesState['search']['request']>) => {
                    setState((s1) => {
                      let a1 = a0(s1.request)
                      let response = a1.kind == 'loaded' && a1.value.data && a1.value.data.posts
                      let pager = a1.kind == 'loaded' && a1.value.data && a1.value.data.pagination

                      if (a1.kind == 'loaded' && a1.value.data?.posts?.length) {
                        window.dataLayer.push({
                          event: 'event.dubbeldam',
                          category: 'Search',
                          action: 'Result',
                          label: s0.snd.header.searchValue,
                        })
                      } else {
                        window.dataLayer.push({
                          event: 'event.dubbeldam',
                          category: 'Search',
                          action: 'No result',
                          label: s0.snd.header.searchValue,
                        })
                      }

                      return {
                        ...s1,
                        request: a0(s1.request),
                        response,
                        pager: pager
                          ? {
                              ...s1.pager,
                              total_items: pager.total_posts,
                              total_pages: pager.total_pages,
                            }
                          : s1.pager,
                      }
                    })
                  }
                )}
              </section>
            </div>
          </div>
        </article>
      )
    }
  })

export { searchResults }
